import { Box, Link } from "@mui/material"
import { formatUnits } from "ethers/lib/utils"
import { filter, reduce } from "lodash"
import React, { useContext, useState } from "react"
import { useTranslation } from "react-i18next"
import Button from "src/components/Button"
import CustomCard from "src/components/CustomCard"
import FerroPriceText from "src/components/FerroPriceText"
import HarvestOptionModal from "src/components/HarvestOptionModal"
import Text from "src/components/Text"
import { POOLS_MAP, FER } from "src/constants"
import { BIG_ZERO } from "src/constants/bigNumber"
import { useBoolean } from "src/hooks/useBoolean"
import { useClaimAllReward } from "src/hooks/useClaimReward"
import { useIsMobile } from "src/hooks/useIsMobile"
import DepositModal from "src/pages/Stake/components/DepositModal"
import useXFerState from "src/pages/Stake/hooks/useXFerState"
import { RewardsBalancesContext } from "src/providers/RewardsBalancesProvider"
import { HarvestMode } from "src/state/xFer/types"
import { getChainId } from "src/utils"
import { formatNumber } from "src/utils/parseStringToBigNumber"
import PieIcon from "src/assets/icons/pie.svg"
import InfoSection from "src/components/InfoSection"
import PoolRow from "./PoolRow"
import { useGetAllBoostReward } from "src/hooks/BoostFarming/useFarmBoostContract"
import { useClaimAllBoostRewards } from "src/hooks/BoostFarming/useFarmBoostHandler"
import { BigNumber } from "ethers"

const PoolSection = () => {
  const { t } = useTranslation()
  const chainId = getChainId()
  const isMobile = useIsMobile()
  const [harvestAmount, setHarvestAmount] = useState<BigNumber>(BIG_ZERO)
  const { claim: claimAll } = useClaimAllReward()
  const [harvest, setHarvest] = useState<() => Promise<void>>(() => claimAll)
  const [showModal, openModal, closeModal] = useBoolean(false)
  const [rewards, rewardTokens] = useContext(RewardsBalancesContext)
  const { openedDepositModal } = useXFerState()

  const pools = filter(POOLS_MAP, (pool) => !!pool.addresses[chainId])
  const basePools = pools.filter(
    (pool) => pool.version !== 2 || !pool.metaSwapAddresses?.[chainId],
  )
  const metaPools = pools.filter(
    (pool) => pool.version === 2 && !!pool.metaSwapAddresses?.[chainId],
  )
  const totalRewards = reduce(rewards, (prev, curr) => prev.add(curr), BIG_ZERO)
  const { data: totalBoostedRewards } = useGetAllBoostReward()
  const { claim: claimAllBoost } = useClaimAllBoostRewards(totalBoostedRewards)

  const openHarvestAllModal = () => {
    setHarvestAmount(totalRewards)
    setHarvest(() => claimAll)
    openModal()
  }

  const openHarvestAllBoostModal = () => {
    setHarvestAmount(totalBoostedRewards ?? BIG_ZERO)
    setHarvest(() => claimAllBoost)
    openModal()
  }

  return (
    <Box mt={3}>
      <CustomCard variant="black50" p={2}>
        <Text size="small">{t("Total Pending Rewards in Pools")}</Text>
        <Box
          display={isMobile ? "block" : "flex"}
          alignItems="flex-end"
          justifyContent="space-between"
        >
          <Box>
            <Text bold my={0.5}>{`${FER.symbol} ${formatNumber(
              formatUnits(totalRewards, FER.decimals),
              2,
              6,
            )}`}</Text>
            <FerroPriceText amount={totalRewards} />
          </Box>
          <Button
            size="small"
            width={isMobile ? "100%" : "auto"}
            onClick={openHarvestAllModal}
            disabled={totalRewards.eq(0)}
            mt={isMobile ? 1 : 0}
          >
            {t("Harvest All")}
          </Button>
        </Box>
        {totalBoostedRewards?.gt(0) && (
          <Box mt={1}>
            <Text size="small">{t("Total Pending Boosted Rewards")}</Text>
            <Box
              display={isMobile ? "block" : "flex"}
              alignItems="flex-end"
              justifyContent="space-between"
            >
              <Box>
                <Text bold my={0.5}>{`${FER.symbol} ${formatNumber(
                  formatUnits(totalBoostedRewards, FER.decimals),
                  2,
                  6,
                )}`}</Text>
                <FerroPriceText amount={totalBoostedRewards} />
              </Box>
              <Button
                size="small"
                width={isMobile ? "100%" : "auto"}
                onClick={openHarvestAllBoostModal}
                disabled={totalRewards.eq(0)}
                mt={isMobile ? 1 : 0}
              >
                {t("Harvest All")}
              </Button>
            </Box>
          </Box>
        )}
      </CustomCard>
      <InfoSection
        marginY="16px"
        content={
          <>
            When you harvest pool rewards, 40% of harvested amount will be
            converted to xFER and vested in one month of lock period. You can
            either choose to harvest remaining 60% directly to your wallet or
            stake into xFER vaults to enjoy higher APR.{" "}
            <Link
              color="#fff"
              fontWeight="bold"
              target="_blank"
              href="https://docs.ferroprotocol.com/user-guides/ferro-stake/lock-usdxfer-into-vaults"
            >
              Learn more
            </Link>
          </>
        }
        img={PieIcon}
      />
      <Box mt={3}>
        <Text color="#ADADAD" textAlign={"start"}>
          Base Pool
        </Text>
        {basePools.map((pool) => (
          <PoolRow
            amount={rewards[pool.name] || BIG_ZERO}
            poolName={pool.name}
            setHarvest={setHarvest}
            setHarvestAmount={setHarvestAmount}
            openModal={openModal}
            key={pool.name}
          />
        ))}
      </Box>
      {metaPools.length > 0 && (
        <Box mt={3} pt={3} borderTop="1px solid #505050">
          <Text color="#ADADAD" textAlign={"start"}>
            Meta Pool
          </Text>
          {metaPools.map((pool) => {
            return (
              <PoolRow
                amount={rewards[pool.name] || BIG_ZERO}
                poolName={pool.name}
                setHarvest={setHarvest}
                setHarvestAmount={setHarvestAmount}
                openModal={openModal}
                key={pool.name}
                rewardsV2={rewardTokens[pool.name]}
              />
            )
          })}
        </Box>
      )}
      <HarvestOptionModal
        open={showModal}
        onClose={closeModal}
        harvest={harvest}
        harvestMode={HarvestMode.Pool}
        harvestAmount={harvestAmount}
      />
      {openedDepositModal && (
        <DepositModal
          defaultSelectedPool={openedDepositModal.defaultSelectedPool}
        />
      )}
    </Box>
  )
}

export default PoolSection
